import { Component, OnDestroy } from '@angular/core';
import { DrawerService } from '../../../../../../../core/services/drawer.service';
import {
  FequencyTimeSlots,
  FrequencyDrawerParams,
} from '../../../insights-data.interface';
import { DRAWER_IDS } from '../../../../../../../core/constants/drawer-ids.constant';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { NzTableModule } from 'ng-zorro-antd/table';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { InsightsDataService } from '../../../insights-data.service';
import { LoaderService } from '../../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../../../core/models/error.models';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import _ from 'lodash';
import { TimeSlot } from '../../../../../../manage-settings/manage-settings.interface';
@Component({
  selector: 'app-frequency-interval',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
    NzSpinModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './frequency-interval.component.html',
  styleUrl: './frequency-interval.component.scss',
})
export class FrequencyIntervalComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  drawerParmas: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  loading$: Observable<boolean> = of(false);
  form!: FormGroup;
  categories = ['Duration', 'Interval Frequency'];
  constructor(
    private drawerService: DrawerService,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private insightsService: InsightsDataService,
    private commonService: CommonService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group({
      dataCategory: ['Interval Frequency'], // Default value for data category
      dataItems: this.fb.array([]), // FormArray for dynamic data items
    });
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.FREQUENCY)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParmas = params;
          this.addDataItem();
        }
      });
  }

  getTitle(startTime: string, endTime: string) {
    return `${startTime} - ${endTime}`;
  }
  get isFormValid() {
    return this.dataItems.controls.some(
      control => control.get('unitValue')?.value.trim() !== ''
    );
  }

  get dataItems() {
    return this.form.get('dataItems') as FormArray;
  }
  get dataItemsControls(): FormGroup[] {
    return this.dataItems?.controls as FormGroup[];
  }
  // Method to add data items dynamically
  addDataItem() {
    // Find all records matching the selected date
    const matchedRecords = _.filter(
      this.drawerParmas.behaviorData.activityDataUnitMultiValues,
      { dataDate: this.drawerParmas.selectedDate, isDurationValue: false }
    );

    let timeSlots: TimeSlot[] | FequencyTimeSlots[] =
      this.drawerParmas.scheduleData.timeSlots;
    if (matchedRecords.length > 0) {
      timeSlots = this.insightsService.generateTimeSlots(matchedRecords);
    }
    if (timeSlots && timeSlots.length > 0) {
      timeSlots.forEach(element => {
        // Find the matched record where both start and end times match
        const matchedRecord = matchedRecords.find(
          record =>
            record.startDataTime.startsWith(element.startTime) &&
            record.endDataTime.startsWith(element.endTime)
        );

        // Create a new FormGroup with either the matched value or an empty string
        const newDataItem = this.fb.group({
          id: [matchedRecord ? matchedRecord.id : 0],
          dataDate: [this.drawerParmas.selectedDate],
          startDataTime: [element.startTime],
          endDataTime: [element.endTime],
          unitValue: [matchedRecord ? matchedRecord.unitValue.toString() : ''], // Set matched unitValue or blank
        });

        this.dataItems.push(newDataItem);
      });
    }
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.FREQUENCY);
  }

  submitForm() {
    let filteredRecords = this.dataItems.value.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: { unitValue: string; [key: string]: any }) => ({
        ...item,
        unitValue: item.unitValue === '0' ? '' : item.unitValue, // Replace '0' with ''
      })
    );
    filteredRecords = _.map(filteredRecords, item => {
      // Construct formatted start and end times
      const formattedStartTime = `${item.startDataTime}:00`; // Setting both start and end time to 11:00:00
      const formattedEndTime = `${item.endDataTime}:00`;

      // Return the updated item with new formatted times
      return _.assign({}, item, {
        startDataTime: formattedStartTime,
        endDataTime: formattedEndTime,
      });
    });
    const payload = {
      unitId: this.drawerParmas.unitId,
      unitMultiValues: filteredRecords,
    };

    this.loaderService.show();
    const operation$ = this.insightsService.saveMultiValue(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `Frequency interval saved successfully`
          );
          this.onCancel();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }
  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
